import _objectSpread from "/usr/app/node_modules/@babel/runtime/helpers/esm/objectSpread2.js";
import React from "react";
var __jsx = React.createElement;
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { z } from "zod";
var DynamicFormContext = /*#__PURE__*/createContext(undefined);
export var createStepSchema = function createStepSchema(step) {
  var baseSchema = {};
  switch (step === null || step === void 0 ? void 0 : step.type) {
    case "radio":
    case "select":
      if (step !== null && step !== void 0 && step.options && (step === null || step === void 0 ? void 0 : step.options.length) > 0) {
        baseSchema[step.field_name] = z["enum"](step.options.map(function (opt) {
          return opt.value;
        }), {
          required_error: "This field is required"
        });
      } else {
        baseSchema[step === null || step === void 0 ? void 0 : step.field_name] = z.string().min(1, "select:required");
      }
      break;
    case "input":
      baseSchema[step === null || step === void 0 ? void 0 : step.field_name] = z.string().min(1, "This field is required");
      break;
    case "currency":
      baseSchema[step === null || step === void 0 ? void 0 : step.field_name] = z.number().refine(function (value) {
        return Number.isFinite(value) && value > 0;
      }, {
        message: "fields/required"
      });
      break;
    default:
      baseSchema[step === null || step === void 0 ? void 0 : step.field_name] = z.any();
  }
  return z.object(baseSchema);
};
export var DynamicFormProvider = function DynamicFormProvider(_ref) {
  var children = _ref.children,
    steps = _ref.steps,
    initialStepId = _ref.initialStepId;
  var _useState = useState({
      visitedSteps: new Set([initialStepId]),
      currentStepId: 1,
      schemas: {},
      disqualify: false,
      steps: steps
    }),
    state = _useState[0],
    setState = _useState[1];
  useEffect(function () {
    var newState = _objectSpread(_objectSpread({}, state), {}, {
      schemas: _objectSpread({}, state.schemas),
      currentStepId: initialStepId,
      visitedSteps: new Set([initialStepId])
    });
    setState(newState);
  }, [initialStepId]);
  var currentStep = useMemo(function () {
    return steps.find(function (step) {
      return step.id === state.currentStepId;
    });
  }, [state.currentStepId, initialStepId]);
  var getNextStepId = function getNextStepId(currentValue) {
    if (!currentStep) return null;
    if (currentStep.options && currentStep.options.length > 0) {
      var option = currentStep.options.find(function (opt) {
        return opt.value === currentValue;
      });
      return option ? option.next_id : currentStep.next_id;
    }
    return currentStep.next_id;
  };
  var goToStep = function goToStep(stepId) {
    var targetStep = steps.find(function (step) {
      return step.id === stepId;
    });
    if (!targetStep) return;
    setState(function (prev) {
      var newVisitedSteps = new Set(prev.visitedSteps).add(stepId);
      var newSchemas = _objectSpread({}, prev.schemas);
      if (!prev.schemas[stepId]) {
        newSchemas[stepId] = createStepSchema(targetStep);
      }
      return _objectSpread(_objectSpread({}, prev), {}, {
        currentStepId: stepId,
        visitedSteps: newVisitedSteps,
        schemas: newSchemas
      });
    });
  };
  var buildCombinedSchema = function buildCombinedSchema() {
    var schemas = {};
    state.visitedSteps.forEach(function (stepId) {
      var step = steps.find(function (s) {
        return s.id === stepId;
      });
      if (step) {
        // @ts-ignore
        Object.assign(schemas, createStepSchema(step).shape);
      }
    });
    return z.object(schemas);
  };
  var value = {
    currentStep: currentStep,
    visitedSteps: state.visitedSteps,
    schema: buildCombinedSchema(),
    goToStep: goToStep,
    getNextStepId: getNextStepId,
    disqualify: state.disqualify,
    state: state,
    setState: setState,
    initialStepId: initialStepId
  };
  return /*#__PURE__*/React.createElement(DynamicFormContext.Provider, {
    value: value
  }, children);
};
export var useDynamicForm = function useDynamicForm() {
  var context = useContext(DynamicFormContext);
  if (!context) {
    throw new Error("useDynamicForm must be used within DynamicFormProvider");
  }
  return context;
};